type CacheEntry<T> = {
  value: T;
  expiresAt: number;
};

export type PersistMode = false | true | 'session' | 'local';

const memory = new Map<string, CacheEntry<unknown>>();
const inflight = new Map<string, Promise<unknown>>();
const STORAGE_PREFIX = 'visime_api_cache:';

function storageKey(key: string): string {
  return `${STORAGE_PREFIX}${key}`;
}

function storageFor(mode: PersistMode): Storage | null {
  if (!mode) return null;
  if (typeof window === 'undefined') return null;
  try {
    if (mode === 'local') return window.localStorage;
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readPersisted<T>(key: string, mode: PersistMode = true): CacheEntry<T> | null {
  const stores: Array<Storage | null> = mode === 'local'
    ? [storageFor('local'), storageFor('session')]
    : [storageFor(mode === false ? false : mode === true ? 'session' : mode)];
  for (const store of stores) {
    if (!store) continue;
    try {
      const raw = store.getItem(storageKey(key));
      if (!raw) continue;
      const parsed = JSON.parse(raw) as CacheEntry<T>;
      if (!parsed || typeof parsed.expiresAt !== 'number') continue;
      return parsed;
    } catch {
      /* ignore */
    }
  }
  return null;
}

function writePersisted<T>(key: string, entry: CacheEntry<T>, mode: PersistMode): void {
  const store = storageFor(mode === true ? 'session' : mode);
  if (!store) return;
  try {
    store.setItem(storageKey(key), JSON.stringify(entry));
  } catch {
    /* quota / private mode */
  }
}

function removePersisted(key: string): void {
  for (const mode of ['session', 'local'] as const) {
    const store = storageFor(mode);
    if (!store) continue;
    try {
      store.removeItem(storageKey(key));
    } catch {
      /* ignore */
    }
  }
}

export function peekApiCache<T>(key: string): T | undefined {
  const mem = memory.get(key) as CacheEntry<T> | undefined;
  if (mem && mem.expiresAt > Date.now()) return mem.value;
  const persisted = readPersisted<T>(key, 'local');
  if (persisted && persisted.expiresAt > Date.now()) {
    memory.set(key, persisted);
    return persisted.value;
  }
  return undefined;
}

export function setApiCache<T>(
  key: string,
  value: T,
  ttlMs: number,
  persist: PersistMode = false
): void {
  const entry: CacheEntry<T> = { value, expiresAt: Date.now() + ttlMs };
  memory.set(key, entry);
  if (persist) writePersisted(key, entry, persist);
}

export function invalidateApiCache(prefixes: string | string[]): void {
  const list = Array.isArray(prefixes) ? prefixes : [prefixes];
  for (const key of Array.from(memory.keys())) {
    if (list.some((p) => key.startsWith(p))) memory.delete(key);
  }
  for (const mode of ['session', 'local'] as const) {
    const store = storageFor(mode);
    if (!store) continue;
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i);
        if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
        const bare = k.slice(STORAGE_PREFIX.length);
        if (list.some((p) => bare.startsWith(p))) toRemove.push(k);
      }
      toRemove.forEach((k) => store.removeItem(k));
    } catch {
      /* ignore */
    }
  }
}

export function clearApiCache(): void {
  memory.clear();
  inflight.clear();
  for (const mode of ['session', 'local'] as const) {
    const store = storageFor(mode);
    if (!store) continue;
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const k = store.key(i);
        if (k?.startsWith(STORAGE_PREFIX)) toRemove.push(k);
      }
      toRemove.forEach((k) => store.removeItem(k));
    } catch {
      /* ignore */
    }
  }
}

type CachedFetchOptions = {
  ttlMs: number;
  /** Persist to sessionStorage (true) or localStorage ('local') for catalogs. */
  persist?: PersistMode;
  /** Allow serving expired cache while a refresh runs (returns stale immediately). */
  staleWhileRevalidate?: boolean;
  /** Max age for stale serve (default 3x ttl). */
  staleTtlMs?: number;
};

/**
 * In-memory (+ optional session/local) TTL cache with in-flight dedupe.
 */
export async function cachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  opts: CachedFetchOptions
): Promise<T> {
  const now = Date.now();
  const staleWindow = opts.staleTtlMs ?? opts.ttlMs * 3;
  const persistMode: PersistMode = opts.persist ?? false;
  const mem = memory.get(key) as CacheEntry<T> | undefined;
  const persisted = persistMode ? readPersisted<T>(key, persistMode) : null;
  const entry = mem && mem.expiresAt >= (persisted?.expiresAt ?? 0) ? mem : persisted ?? mem;

  if (entry && entry.expiresAt > now) {
    if (entry !== mem) memory.set(key, entry);
    return entry.value;
  }

  const canServeStale =
    Boolean(opts.staleWhileRevalidate) && entry != null && entry.expiresAt + staleWindow > now;

  const runFetch = () => {
    const existing = inflight.get(key) as Promise<T> | undefined;
    if (existing) return existing;
    const p = fetcher()
      .then((value) => {
        setApiCache(key, value, opts.ttlMs, persistMode);
        return value;
      })
      .finally(() => inflight.delete(key));
    inflight.set(key, p);
    return p;
  };

  if (canServeStale && entry) {
    void runFetch();
    return entry.value;
  }

  return runFetch();
}

export function removeApiCacheKey(key: string): void {
  memory.delete(key);
  removePersisted(key);
}
